/**
 * User (lawyer) API — axios with Bearer token interceptor for end-user auth.
 * Separate from adminApi.js (different localStorage key + different JWT type).
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "lexiq_user_token";

export const getUserToken = () => localStorage.getItem(TOKEN_KEY) || "";
export const setUserToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearUserToken = () => localStorage.removeItem(TOKEN_KEY);

export const userApi = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

userApi.interceptors.request.use((config) => {
  const t = getUserToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

userApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) clearUserToken();
    return Promise.reject(err);
  },
);

export const userSignup = (payload) =>
  userApi.post("/auth/signup", payload).then((r) => r.data);

/* تَجديد بِطاقة نِقابة المحامين للمُستَخدِم المُوَثَّق حالياً. Requires
 * a valid session cookie / bearer token — the server verifies that the
 * new scan's bar_id_number matches the current account's, so lawyers
 * can't renew someone else's card. */
export const renewBarCard = (payload) =>
  userApi.post("/auth/me/bar-card/renew", payload).then((r) => r.data);

export const clientSignup = (payload) =>
  userApi.post("/auth/signup-client", payload).then((r) => r.data);

export const acceptTerms = (email) =>
  userApi
    .post("/auth/terms/accept", {
      email,
      agreement_accepted: true,
    })
    .then((r) => r.data);

export const verifyOtp = (email, code) =>
  userApi.post("/auth/verify-otp", { email, code }).then((r) => r.data);

export const resendOtp = (email) =>
  userApi.post("/auth/resend-otp", { email }).then((r) => r.data);

export const forgotPassword = (email) =>
  userApi.post("/auth/forgot-password", { email }).then((r) => r.data);

export const resendResetOtp = (email) =>
  userApi.post("/auth/resend-reset-otp", { email }).then((r) => r.data);

export const resetPassword = (email, code, new_password) =>
  userApi
    .post("/auth/reset-password", { email, code, new_password })
    .then((r) => r.data);

export const userLogin = (email, password) =>
  userApi.post("/auth/login", { email, password }).then((r) => r.data);

export const userMe = () => userApi.get("/auth/me").then((r) => r.data);

export const userLogout = () =>
  userApi.post("/auth/logout").then((r) => r.data).catch(() => null);

// Presence ping — frontend calls this every ~45s while the app is in the
// foreground so the admin dashboard can show real-time online status.
export const userHeartbeat = (platform = "web") =>
  userApi.post("/auth/heartbeat", { platform }).then((r) => r.data).catch(() => null);

export const userUpdateMe = (payload) =>
  userApi.put("/auth/me", payload).then((r) => r.data);

export const userDeleteMe = (password) =>
  userApi.delete("/auth/me", { data: { password } }).then((r) => r.data);

export const formatUserApiError = (err) => {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "خطأ غير متوقع";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((x) => x?.msg || JSON.stringify(x)).join(" — ");
  }
  return String(d);
};
